import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { BarChart3, TrendingUp, AlertTriangle, Flame, Users, Clock, CheckCircle, Activity, Target, Zap, FileText, PieChart as PieChartIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { SiteFilter } from '@/components/admin/SiteFilter';
import { RiskPulseWidget } from '@/components/admin/RiskPulseWidget';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUserContext } from '@/hooks/useUserContext';

interface AdminAnalyticsProps {
  siteId?: string | null;
}

const AdminAnalytics = ({ siteId: propSiteId }: AdminAnalyticsProps = {}) => {
  const { companyId } = useUserContext();
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string>(propSiteId || 'all');
  const [analytics, setAnalytics] = useState({
    totalReports: 0,
    verifiedReports: 0,
    pendingReports: 0,
    rejectedReports: 0,
    highRiskReports: 0,
    activeUsers: 0,
    checkinRate: 0,
    avgResolutionTime: 0,
    avgQualityScore: 0,
    autoConfirmedRate: 0,
  });
  const [hazardTrends, setHazardTrends] = useState<any[]>([]);
  const [categoryDistribution, setCategoryDistribution] = useState<any[]>([]);
  const [causeDistribution, setCauseDistribution] = useState<any[]>([]);
  const [scoreDistribution, setScoreDistribution] = useState<any[]>([]);
  const [engagementData, setEngagementData] = useState<any[]>([]);
  const [topLocations, setTopLocations] = useState<any[]>([]);
  const [interventionStats, setInterventionStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    completionRate: 0,
    avgEffectiveness: 0,
    improved: 0,
    stable: 0,
    declined: 0,
  });

  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteFilter(propSiteId);
    }
  }, [propSiteId]);

  useEffect(() => {
    if (companyId) {
      fetchAnalytics();
    }
  }, [selectedSiteFilter, companyId]);

  const fetchAnalytics = async () => {
    if (!companyId) return;
    
    try {
      const siteFilter = selectedSiteFilter !== 'all' ? selectedSiteFilter : null;
      
      // First get all site IDs for this company
      const { data: companySites } = await supabase
        .from('company_locations')
        .select('id')
        .eq('company_id', companyId);
      
      const companySiteIds = companySites?.map((s: any) => s.id) || [];
      
      // If no sites, return empty data
      if (companySiteIds.length === 0) {
        console.log('No sites found for company');
        return;
      }
      
      // Determine which site IDs to filter by
      const filterSiteIds = siteFilter ? [siteFilter] : companySiteIds;
      
      // Base queries - all filtered by site_id(s) belonging to company
      let allReportsQuery = supabase.from('peer_reports').select('id', { count: 'exact', head: true }).in('site_id', filterSiteIds);
      let verifiedQuery = supabase.from('peer_reports').select('id', { count: 'exact', head: true }).in('site_id', filterSiteIds).eq('verification_status', 'hazard_confirmed');
      let pendingQuery = supabase.from('peer_reports').select('id', { count: 'exact', head: true }).in('site_id', filterSiteIds).in('verification_status', ['pending_verification', 'peer_corroboration_requested']);
      let rejectedQuery = supabase.from('peer_reports').select('id', { count: 'exact', head: true }).in('site_id', filterSiteIds).eq('verification_status', 'rejected');
      let highRiskQuery = supabase.from('peer_reports').select('id', { count: 'exact', head: true }).in('site_id', filterSiteIds).in('risk_rating', ['high', 'very_high']);
      let allReportsDataQuery = supabase.from('peer_reports').select('created_at, hazard_category, risk_rating, hazard_classification, location, reviewed_at, verification_status, evidence_score, cause_category').in('site_id', filterSiteIds);
      let usersQuery = supabase.from('worker_profiles').select('id', { count: 'exact', head: true }).eq('company_id', companyId);
      let checkinsQuery = supabase.from('shift_checkins').select('id', { count: 'exact', head: true }).in('site_id', filterSiteIds).eq('check_in_date', new Date().toISOString().split('T')[0]);
      let interventionsQuery = supabase.from('interventions').select('id, status, effectiveness_score, safety_state_at_delivery, safety_state_after_30d').eq('company_id', companyId);

      if (siteFilter) {
        usersQuery = supabase.from('worker_profiles').select('id', { count: 'exact', head: true }).eq('site_id', siteFilter);
        interventionsQuery = supabase.from('interventions').select('id, status, effectiveness_score, safety_state_at_delivery, safety_state_after_30d').eq('site_id', siteFilter);
      }

      const [allReports, verified, pending, rejected, highRisk, allReportsData, users, checkins, interventions] = await Promise.all([
        allReportsQuery,
        verifiedQuery,
        pendingQuery,
        rejectedQuery,
        highRiskQuery,
        allReportsDataQuery,
        usersQuery,
        checkinsQuery,
        interventionsQuery,
      ]);

      const totalUsers = users.count || 0;
      const todayCheckins = checkins.count || 0;
      const checkinRate = totalUsers > 0 ? Math.round((todayCheckins / totalUsers) * 100) : 0;

      // Calculate avg resolution time (hours between created_at and reviewed_at)
      let avgResTime = 0;
      const resolvedReports = allReportsData.data?.filter(r => r.reviewed_at) || [];
      if (resolvedReports.length > 0) {
        const totalTime = resolvedReports.reduce((sum, r) => {
          const created = new Date(r.created_at).getTime();
          const reviewed = new Date(r.reviewed_at).getTime();
          return sum + (reviewed - created);
        }, 0);
        avgResTime = Math.round(totalTime / resolvedReports.length / (1000 * 60 * 60));
      }

      // Calculate quality score stats
      const reportsWithScore = allReportsData.data?.filter(r => r.evidence_score != null) || [];
      const avgScore = reportsWithScore.length > 0 
        ? Math.round(reportsWithScore.reduce((sum, r) => sum + (r.evidence_score || 0), 0) / reportsWithScore.length)
        : 0;
      const autoConfirmed = allReportsData.data?.filter(r => r.verification_status === 'hazard_confirmed').length || 0;
      const autoRate = allReportsData.data?.length ? Math.round((autoConfirmed / allReportsData.data.length) * 100) : 0;

      // Intervention stats with effectiveness
      const interventionData = interventions.data || [];
      const completedInterventions = interventionData.filter(i => i.status === 'completed').length;
      const pendingInterventions = interventionData.filter(i => i.status === 'pending' || i.status === 'active').length;
      
      // Calculate state transitions
      const stateValues: Record<string, number> = { safe: 4, at_risk: 3, high_risk: 2, chronic: 1 };
      const withStateData = interventionData.filter(i => i.safety_state_at_delivery && i.safety_state_after_30d);
      const improved = withStateData.filter(i => (stateValues[i.safety_state_after_30d] || 0) > (stateValues[i.safety_state_at_delivery] || 0)).length;
      const stable = withStateData.filter(i => i.safety_state_after_30d === i.safety_state_at_delivery).length;
      const declined = withStateData.filter(i => (stateValues[i.safety_state_after_30d] || 0) < (stateValues[i.safety_state_at_delivery] || 0)).length;
      const withEffectiveness = interventionData.filter(i => i.effectiveness_score != null);
      const avgEffectiveness = withEffectiveness.length > 0
        ? Math.round(withEffectiveness.reduce((sum, i) => sum + (i.effectiveness_score || 0), 0) / withEffectiveness.length)
        : 0;

      setAnalytics({
        totalReports: allReports.count || 0,
        verifiedReports: verified.count || 0,
        pendingReports: pending.count || 0,
        rejectedReports: rejected.count || 0,
        highRiskReports: highRisk.count || 0,
        activeUsers: totalUsers,
        checkinRate,
        avgResolutionTime: avgResTime,
        avgQualityScore: avgScore,
        autoConfirmedRate: autoRate,
      });

      setInterventionStats({
        total: interventionData.length,
        completed: completedInterventions,
        pending: pendingInterventions,
        completionRate: interventionData.length > 0 ? Math.round((completedInterventions / interventionData.length) * 100) : 0,
        avgEffectiveness,
        improved,
        stable,
        declined,
      });

      // Category distribution
      const categoryMap: Record<string, number> = {};
      allReportsData.data?.forEach((report) => {
        const category = report.hazard_category?.replace(/_/g, ' ') || 'Unknown';
        categoryMap[category] = (categoryMap[category] || 0) + 1;
      });
      const categoryData = Object.entries(categoryMap).map(([name, value]) => ({ name, value }));
      setCategoryDistribution(categoryData);

      // Cause distribution (NEW)
      const causeMap: Record<string, number> = {};
      allReportsData.data?.forEach((report) => {
        const cause = report.cause_category?.replace(/_/g, ' ') || 'Not specified';
        causeMap[cause] = (causeMap[cause] || 0) + 1;
      });
      const causeData = Object.entries(causeMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
      setCauseDistribution(causeData);

      // Score distribution (NEW)
      const scoreBuckets = { 'Auto-Confirmed (≥55)': 0, 'Peer Review (25-54)': 0, 'Pending (<25)': 0 };
      allReportsData.data?.forEach((report) => {
        const score = report.evidence_score || 0;
        if (score >= 55) scoreBuckets['Auto-Confirmed (≥55)']++;
        else if (score >= 25) scoreBuckets['Peer Review (25-54)']++;
        else scoreBuckets['Pending (<25)']++;
      });
      const scoreData = Object.entries(scoreBuckets).map(([name, value]) => ({ name, value }));
      setScoreDistribution(scoreData);

      // Location hotspots
      const locationMap: Record<string, number> = {};
      allReportsData.data?.forEach((report) => {
        const loc = report.location || 'Unknown';
        locationMap[loc] = (locationMap[loc] || 0) + 1;
      });
      const locationData = Object.entries(locationMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      setTopLocations(locationData);

      // Monthly trends
      const monthlyMap: Record<string, number> = {};
      allReportsData.data?.forEach((report) => {
        const month = new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        monthlyMap[month] = (monthlyMap[month] || 0) + 1;
      });
      const trendData = Object.entries(monthlyMap).map(([month, reports]) => ({ month, reports }));
      setHazardTrends(trendData.slice(-6));

      // Weekly engagement (last 7 days)
      const weeklyEngagement: any[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayReports = allReportsData.data?.filter(r => r.created_at.startsWith(dateStr)).length || 0;
        weeklyEngagement.push({
          day: date.toLocaleDateString('en-US', { weekday: 'short' }),
          reports: dayReports,
        });
      }
      setEngagementData(weeklyEngagement);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];
  const SCORE_COLORS = ['#10b981', '#f59e0b', '#ef4444'];

  return (
    <div className="space-y-6">
      {/* Site Filter */}
      {!propSiteId && (
        <div className="flex justify-end">
          <SiteFilter 
            value={selectedSiteFilter} 
            onChange={setSelectedSiteFilter}
            className="w-[200px]"
          />
        </div>
      )}

      {/* Risk Pulse Widget - Prominent at top */}
      <RiskPulseWidget siteId={selectedSiteFilter !== 'all' ? selectedSiteFilter : undefined} />

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Total Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-3xl font-bold">{analytics.totalReports}</p>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Verified
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-3xl font-bold text-emerald-600">{analytics.verifiedReports}</p>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-amber-500" />
              Avg Quality Score
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-3xl font-bold text-amber-600">{analytics.avgQualityScore}</p>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-rose-500" />
              High Risk
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-3xl font-bold text-rose-600">{analytics.highRiskReports}</p>
          </CardContent>
        </Card>
      </div>

      {/* Report Quality & Cause Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PieChartIcon className="h-4 w-4 text-primary" />
              Report Quality Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={160}>
                <PieChart>
                  <Pie
                    data={scoreDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {scoreDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SCORE_COLORS[index % SCORE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {scoreDistribution.map((item, idx) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SCORE_COLORS[idx] }} />
                    <span className="text-xs flex-1">{item.name}</span>
                    <span className="text-xs font-bold">{item.value}</span>
                  </div>
                ))}
                <div className="pt-2 border-t border-border/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Auto-Confirm Rate</span>
                    <span className="font-bold text-emerald-600">{analytics.autoConfirmedRate}%</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-amber-500" />
              Cause Analysis Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {causeDistribution.slice(0, 5).map((cause, idx) => (
                <div key={cause.name} className="flex items-center gap-2">
                  <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0 text-xs">
                    {idx + 1}
                  </Badge>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize">{cause.name}</span>
                      <span className="font-medium">{cause.value}</span>
                    </div>
                    <Progress 
                      value={(cause.value / (causeDistribution[0]?.value || 1)) * 100} 
                      className="h-1.5 mt-1" 
                    />
                  </div>
                </div>
              ))}
              {causeDistribution.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No cause data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Engagement & Intervention Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Worker Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Workers</span>
                <span className="font-medium">{analytics.activeUsers}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Today's Check-in Rate</span>
                <div className="flex items-center gap-2">
                  <Progress value={analytics.checkinRate} className="w-20 h-2" />
                  <span className="font-medium">{analytics.checkinRate}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Avg Resolution Time</span>
                <span className="font-medium">{analytics.avgResolutionTime}h</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pending Review</span>
                <Badge variant={analytics.pendingReports > 5 ? "destructive" : "secondary"}>
                  {analytics.pendingReports}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-violet-500" />
              Intervention Effectiveness
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Completion Rate</span>
                <div className="flex items-center gap-2">
                  <Progress value={interventionStats.completionRate} className="w-20 h-2" />
                  <span className="font-medium">{interventionStats.completionRate}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Avg Effectiveness</span>
                <span className="font-medium">{interventionStats.avgEffectiveness}%</span>
              </div>
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-2">State Transitions (30d)</p>
                <div className="flex gap-2">
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                    ↑ {interventionStats.improved}
                  </Badge>
                  <Badge variant="outline" className="bg-muted text-muted-foreground">
                    → {interventionStats.stable}
                  </Badge>
                  <Badge variant="outline" className="bg-rose-500/10 text-rose-600 border-rose-500/30">
                    ↓ {interventionStats.declined}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hazard Hotspots */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-rose-500" />
            Hazard Hotspots
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topLocations.map((loc, idx) => (
              <div key={loc.name} className="flex items-center gap-3">
                <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                  {idx + 1}
                </Badge>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{loc.name}</span>
                    <span className="text-sm text-muted-foreground">{loc.count} reports</span>
                  </div>
                  <Progress value={(loc.count / (topLocations[0]?.count || 1)) * 100} className="h-1.5 mt-1" />
                </div>
              </div>
            ))}
            {topLocations.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No location data available</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Weekly Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Area type="monotone" dataKey="reports" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5" />
              Hazard Category Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={categoryDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border/50 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Monthly Report Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={hazardTrends}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="month" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                <Legend />
                <Line type="monotone" dataKey="reports" stroke="hsl(var(--primary))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
